import React from "react";
import { COULEUR, PhraseAbsence } from "./_commun";

// Journal — ce qu'il a fait, ligne par ligne, chacune menant à son écran.
//
// Ce qu'il apprend (registre) : « ce qu'il a fait, ligne par ligne, chacune
// menant à son écran ». Dimitri, 2026-09-13 : « quatre lignes horodatées,
// chacune cliquable vers le bon écran ».
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     lignes: [                                   // 0 à 6, les plus récentes d'abord
//       { quand:  "2026-09-12T12:32:00.000Z",     // ISO (ou null pour une panne)
//         heure:  "sam. 12 sept., 14:32",         // l'heure de Paris, rendue serveur
//         quoi:   "a classé 45 courriels",
//         detail: "5 attendent une réponse",      // ou null
//         ecran:  "operateur",                    // une clé de lib/app-carte.js
//         cible:  null },                         // identifiant, ou null
//       …
//     ],
//     vide: "je n'ai rien fait que je sache tracer depuis hier soir" | null
//   }
//
// ⚠️ UNE LISTE VIDE N'EST PAS DESSINÉE : c'est la phrase `vide` du serveur
// qui s'affiche — elle dit « rien », et elle le dit depuis quand. Sans cette
// phrase ET sans ligne, ce n'est pas « rien », c'est une donnée malformée, et
// on le dit aussi.
//
// ⚠️ LE DESSIN NE NAVIGUE PAS LUI-MÊME : il rend l'INTENTION
// `{ quoi: "ecran", ecran, cible }` à `onAller` ; l'écran décide. Sans
// `onAller`, les lignes ne sont pas cliquables — un curseur qui promet un clic
// sans rien faire est pire qu'un curseur inerte.

const LIGNES_MAX = 6;

function estTexte(s, max) {
  return typeof s === "string" && s.trim().length > 0 && s.length <= max;
}

function validerJournal(d) {
  if (!d || typeof d !== "object" || !Array.isArray(d.lignes))
    return { erreur: "pas de journal à montrer" };
  if (d.lignes.length === 0) {
    if (estTexte(d.vide, 200)) return { vide: d.vide.trim() };
    return {
      erreur:
        "journal vide sans explication — je ne sais pas si c'est « rien »",
    };
  }
  const lignes = [];
  for (const l of d.lignes.slice(0, LIGNES_MAX)) {
    if (
      !l ||
      typeof l !== "object" ||
      !estTexte(l.quoi, 200) ||
      !estTexte(l.ecran, 40)
    )
      return {
        erreur: "données incomplètes — une ligne n'a ni action ni écran",
      };
    lignes.push({
      quand: typeof l.quand === "string" ? l.quand : null,
      heure: estTexte(l.heure, 40) ? l.heure : null,
      quoi: l.quoi.trim(),
      detail: estTexte(l.detail, 240) ? l.detail.trim() : null,
      ecran: l.ecran,
      cible: l.cible == null ? null : String(l.cible).slice(0, 60),
    });
  }
  return { lignes };
}

export default function Journal({ titre, donnees, onAller }) {
  const cliquable = typeof onAller === "function";
  const v = validerJournal(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  if (v.vide) return <PhraseAbsence titre={titre} texte={v.vide} />;
  const { lignes } = v;
  const conclusion = `${titre} : ${lignes.length} ligne${lignes.length > 1 ? "s" : ""}`;

  return (
    <figure
      data-testid="dessin-journal"
      aria-label={conclusion}
      style={{ margin: "6px 0 10px", padding: 0, maxWidth: 560 }}
    >
      <figcaption
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: COULEUR.navyD,
          margin: "0 0 4px",
        }}
      >
        {titre}
      </figcaption>
      <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {lignes.map((l, i) => {
          const cible = { quoi: "ecran", ecran: l.ecran, cible: l.cible };
          const ouvrir = cliquable ? () => onAller(cible) : undefined;
          return (
            <li
              key={`${l.quand || "panne"}-${i}`}
              data-testid="journal-ligne"
              data-ecran={l.ecran}
              role={cliquable ? "button" : undefined}
              tabIndex={cliquable ? 0 : undefined}
              aria-label={cliquable ? `${l.quoi} — ouvrir l'écran` : undefined}
              onClick={ouvrir}
              onKeyDown={
                cliquable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onAller(cible);
                      }
                    }
                  : undefined
              }
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: "0 10px",
                padding: "6px 8px",
                borderTop: i === 0 ? "none" : `1px solid ${COULEUR.bord}`,
                cursor: cliquable ? "pointer" : "default",
                borderRadius: 4,
              }}
            >
              <span
                data-testid="journal-heure"
                style={{
                  fontSize: 11.5,
                  color: COULEUR.gris,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {l.heure || "—"}
              </span>
              <span style={{ fontSize: 13, color: COULEUR.navyD }}>
                <span data-testid="journal-quoi">{l.quoi}</span>
                {l.detail && (
                  <span
                    data-testid="journal-detail"
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: COULEUR.grisD,
                      marginTop: 2,
                    }}
                  >
                    {l.detail}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}
